import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Paper, TextField, Button, Snackbar, SnackbarContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import { useCommentOverlayStore } from '../stores/CommentStore';
import { styled } from '@mui/material/styles';
import { useComments, useCreateComment, useDeleteComment } from '../utils/comments/comments';

interface AnimatedBoxProps {
    isScrolled: boolean;
}

interface CommentsOverlayProps {
    open: boolean;
    onClose: () => void;
}

const AnimatedBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isScrolled',
})<AnimatedBoxProps>(({ theme, isScrolled }) => ({
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: '500px',
    backgroundColor: theme.palette.customText.logoInvert,
    overflow: 'auto',
    zIndex: theme.zIndex.drawer + 1,
    height: isScrolled ? '100%' : `calc(100% - 48px)`,
    paddingTop: isScrolled ? "48px" : '0',
    boxShadow: theme.shadows[5],
}));

const CommentsOverlay: React.FC<CommentsOverlayProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const [newComment, setNewComment] = useState({ message: '' });
    const [showFilter, setShowFilter] = useState(false);
    const [textFilter, setTextFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [isScrolled, setIsScrolled] = useState(false);

    // Use the API hooks
    const { data: comments = [], isLoading } = useComments();
    const createCommentMutation = useCreateComment();
    const deleteCommentMutation = useDeleteComment();

    const OverlayColor = theme.palette.customText.logoInvert;
    const selectedTileId = useCommentOverlayStore(state => state.selectedTileId);

    const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewComment({ ...newComment, [e.target.name]: e.target.value });
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 25);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createCommentMutation.mutateAsync({
                header: selectedTileId ?? 'Keine Kachel ausgewählt',
                text: newComment.message
            });
            setNewComment({ message: '' });
        } catch (error) {
            console.error('Error creating comment:', error);
        }
    };

    const toLocalDateString = (dateString: string) => {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteCommentMutation.mutateAsync(id.toString());
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const toggleFilterSnackbar = () => {
        setShowFilter(!showFilter);
    };

    const applyFilter = () => {
        setShowFilter(false);
    };

    // Ensure comments is always an array and add additional safety
    const safeComments = Array.isArray(comments) ? comments : [];

    // Add safety check for filter operations
    const filteredComments = safeComments.filter(comment => {
        // Ensure comment object has required properties
        if (!comment || typeof comment !== 'object') return false;

        const textMatch = textFilter === '' ||
            (comment.text && comment.text.toLowerCase().includes(textFilter.toLowerCase())) ||
            (comment.header && comment.header.toLowerCase().includes(textFilter.toLowerCase()));

        const dateMatch = dateFilter === '' || comment.timestamp === dateFilter;

        return textMatch && dateMatch;
    });

    const sortedComments = [...filteredComments].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    const onDateChange = (value: string) => {
        setDateFilter(value);
    };

    // Add additional safety check
    if (!open || !safeComments) return null;

    return (
        <AnimatedBox isScrolled={isScrolled}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>
            <Typography variant="h6" sx={{ paddingLeft: 2 }}>Kommentar schreiben</Typography>
            <Typography variant="h5" sx={{ paddingLeft: 2 }}>Bitte wählen Sie eine Kachel aus, zu der Sie einen Kommentar verfassen möchten.</Typography>
            <Box component="form" sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }} onSubmit={handleSubmit}>
                <TextField variant='standard' name="message" label="Nachricht schreiben" fullWidth multiline rows={6} sx={{
                    mb: 2,
                    '& .MuiInputBase-root': {
                        width: '100% !important'
                    }
                }} value={newComment.message} onChange={handleCommentChange} />
                <Button type="submit" variant="outlined" color="primary">Absenden</Button>
            </Box>

            <Snackbar open={showFilter} autoHideDuration={6000} onClose={toggleFilterSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }} >
                <SnackbarContent sx={{ width: '100%', maxWidth: '600px', backgroundColor: OverlayColor }} message={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Nach Titel filtern" variant='standard' fullWidth value={textFilter} onChange={(e) => setTextFilter(e.target.value)} />
                        <TextField
                            variant='standard'
                            label="Nach Zeit filtern"
                            type="date"
                            value={dateFilter ? dateFilter.split('.').reverse().join('-') : ''}
                            onChange={(e) => onDateChange(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Button onClick={applyFilter} color="primary" variant="contained">Filter anwenden</Button>
                    </Box>
                } />
            </Snackbar>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: 2, paddingRight: 2 }}>
                <Typography variant="h6">Alle Kommentare</Typography>
                <Box>
                    <Button startIcon={<FilterListIcon />} onClick={toggleFilterSnackbar} variant="contained" sx={{ m: 1, backgroundColor: theme.palette.customText.logoColored }}></Button>
                </Box>
            </Box>

            {isLoading ? (
                <Box sx={{ p: 2 }}>
                    <Typography>Kommentare werden geladen...</Typography>
                </Box>
            ) : (
                sortedComments.map((comment) => (
                    <Paper key={comment.id} sx={{ margin: 2, padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', backgroundColor: "transparent" }}>
                        <Box sx={{ width: '10%', display: 'flex', justifyContent: 'flex-start', alignItems: 'start', flexDirection: 'column', paddingTop: '8px' }}>
                            <Box sx={{
                                position: 'absolute',
                                top: '1rem',
                                left: '1rem',
                                backgroundColor: "rgba(0, 22, 26, 0.54)",
                                borderRadius: '8px',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                height: '2.5rem',
                                width: '2.5rem',
                                padding: '0.5rem',
                                boxShadow: "0px 2px 8px 1px rgba(1,52,61,0.15)",
                            }}>
                                <Typography variant="h5" sx={{ color: '#fff', textAlign: 'center' }}>
                                    {new Date(comment.timestamp).getDate()}
                                </Typography>
                                <Typography variant="h4" sx={{ color: '#fff', textAlign: 'left' }}>
                                    {new Date(comment.timestamp).toLocaleString('default', { month: 'short' }).toUpperCase()}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ width: '90%', paddingLeft: 1 }}>
                            <Typography variant="h2">{comment.header}</Typography>
                            <Typography variant="h4" color="textSecondary">
                                {toLocalDateString(comment.timestamp)}
                            </Typography>
                            <Typography variant="h5">{comment.text}</Typography>
                        </Box>
                        <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={() => handleDelete(comment.id)}><DeleteIcon /></IconButton>
                    </Paper>
                ))
            )}
        </AnimatedBox>
    );
};

export default CommentsOverlay;